import { useEffect, useRef } from 'react';

/** Quanto o palco encolhe ao encaixar no topo (fica com 40% do tamanho). */
const DOCKED_SCALE = 0.4;
/** Folga entre o topo visível (ou a barra do app) e o palco encaixado. */
const GAP = 8;

function scrollerOf(node: HTMLElement): HTMLElement | null {
  let current = node.parentElement;
  while (current && current !== document.body) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

function safeTop(): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;padding-top:env(safe-area-inset-top,0px);visibility:hidden';
  document.body.appendChild(probe);
  const value = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return value;
}

/**
 * Palco do personagem que acompanha a rolagem: conforme o dedo sobe, ele sobe e
 * encolhe ao mesmo tempo até encaixar no topo com 40% do tamanho, centrado; o
 * resto da tela passa por trás dele. O movimento é ligado ao dedo (sem animação
 * própria): segurar controla, soltar deixa onde parou, e voltar ao topo o devolve
 * ao tamanho cheio.
 *
 * O palco fica preso (`position: sticky`) na própria posição desde o começo e o
 * movimento é só `transform` (translateY + scale), então nada muda no layout e o
 * texto embaixo acompanha a borda do palco a uma distância constante até ele
 * encaixar.
 */
export function useDockingStage() {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    const dock = dockRef.current;
    if (!anchor || !dock) return;
    const scroller = scrollerOf(dock);
    const scrollTop = () => (scroller ? scroller.scrollTop : window.scrollY);
    const viewTop = () => (scroller ? scroller.getBoundingClientRect().top : 0);

    let travel = 1;
    let frame = 0;
    const apply = () => {
      frame = 0;
      const progress = Math.min(1, Math.max(0, scrollTop() / travel));
      dock.style.setProperty('--p', progress.toFixed(4));
      dock.classList.toggle('docked', progress >= 1);
    };
    const measure = () => {
      const hero = dock.querySelector<HTMLElement>('.mj-hero');
      if (!hero) return;
      // Posição natural do palco no topo da rolagem e o ponto onde ele encaixa.
      const natural = anchor.getBoundingClientRect().top - viewTop() + scrollTop();
      // Dentro do app, a área que rola já começa abaixo da barra do topo; na primeira
      // entrada (sem a barra), a tela inteira rola e o topo é a área segura do aparelho.
      const target = (scroller ? 0 : safeTop()) + GAP;
      const lift = Math.max(0, natural - target);
      const height = hero.offsetHeight;
      // Percurso em que o palco sobe e encolhe junto com o texto, sem abrir nem fechar a folga.
      travel = Math.max(1, lift + height * (1 - DOCKED_SCALE));
      dock.style.setProperty('--mj-natural', `${natural}px`);
      dock.style.setProperty('--mj-lift', `${lift}px`);
      dock.style.setProperty('--mj-hero-h', `${height}px`);
      apply();
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };

    measure();
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(dock);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, []);

  return { anchorRef, dockRef };
}
